/**
 * Created by maciej on 30/06/2016.
 */
import {app} from '../app';
import {ISessionService} from "./SessionService";
import {SharedQuestionService} from "./SharedQuestionService";
import {SharedQuestion} from "../sharedQuestions/SharedQuestion";
import {ITranslationService} from "./TranslationService";

export interface IQuestionnaireLogicService {
    setQuestionnaire(questionnaire:IQuestionnaire):ng.IPromise<any>;
    getCurrentQuestion():IQuestion;

    resume():ng.IPromise<any>;
    nextQuestion():ng.IPromise<any>;
    previousQuestion():ng.IPromise<any>;
    previousQuestionJump(id:number):ng.IPromise<any>;
    jumpBack():ng.IPromise<any>;
    skipQuestion();
    isLastQuestion():boolean;
    isFirstQuestion():boolean;
    canSaveInBackground():boolean;

    getProgress():ISessionProgress;
    getPreviousQuestions():IQuestion[];

    onNextQuestion(callback:() => boolean):() => void;
}

export class QuestionnaireLogicService implements IQuestionnaireLogicService {

    private http:ng.IHttpService;
    private q:ng.IQService;
    private sessionService:ISessionService;
    private sharedQuestionService:SharedQuestionService;
    private translationService:ITranslationService;

    private questionnaire:IQuestionnaire; // @TODO: Accessors (https: //www.typescriptlang.org/docs/handbook/classes.html)

    private previousQuestions:IQuestion[]                = [];
    private currentQuestion:IQuestion                    = null;
    private nextQuestions:IQuestion[]                    = [];
    private reloadNextQuestionsAfterQuestionIds:number[] = [];
    private firstNextQuestion:boolean                    = true;

    private nextQuestionCallbacks:(() => any)[] = [];

    private _isLastQuestion:boolean     = false;
    private lastQuestionInNextQuestions = false;
    private progress:ISessionProgress   = {
        currentQuestion: 1,
        answered       : 1,
        questions      : 1,
    };

    constructor($http:ng.IHttpService, $q:ng.IQService, sessionService:ISessionService, sharedQuestionService:SharedQuestionService, TranslationService:ITranslationService) {
        this.http                  = $http;
        this.q                     = $q;
        this.sessionService        = sessionService;
        this.sharedQuestionService = sharedQuestionService;
        this.translationService    = TranslationService;

        this.sessionService.onSaveAnswer((answer) => {
            // if after this question id previously new next questions were get then force the same behaviour
            if (!this.canSaveInBackground()) {
                this.nextQuestions = [];
            }
        });
    }

    public setQuestionnaire(questionnaire:IQuestionnaire):ng.IPromise<any> {
        this.questionnaire = questionnaire;

        return this.http({
            method: 'GET',
            url   : '/api/session/' + this.sessionService.activeSession.id + '/flow/last',
            params: {
                locale: this.translationService.getCurrentLanguage().locale
            }
        }).then((value:any) => {
            this.currentQuestion = this.mapQuestions([value.data.question])[0];
            this.progress        = value.data.progress;

            return this.q.resolve();
        });
    }

    getCurrentQuestion():IQuestion {
        return this.currentQuestion;
    }

    public resume():ng.IPromise<any> {
        return this.http({
            method: 'GET',
            url   : '/api/session/' + this.sessionService.activeSession.id + '/flow/previousSet',
            params: {
                locale: this.translationService.getCurrentLanguage().locale
            }
        }).then((value:any) => {

            this.previousQuestions = this.mapQuestions(value.data.questions);

        });
    }

    public nextQuestion():ng.IPromise<any> {
        if (this.isLastQuestion()) {
            return this.q.reject();
        }

        for (let callback of this.nextQuestionCallbacks) {
            if (callback() === false) {
                return this.q.reject();
            }
        }

        const pushCurrentQuestion = () => {
            this.previousQuestions.push(this.currentQuestion);
            this.currentQuestion                 = this.nextQuestions.shift();
            this.sharedQuestionService.questions = [];
            return this.q.resolve();
        };

        if (this.canSaveInBackground()) {
            if (this.currentQuestion.meta.progress !== false) {
                this.progress.currentQuestion++;
            }


            if (this.nextQuestions.length == 0) {
                this._isLastQuestion = this.lastQuestionInNextQuestions;
            }

            return pushCurrentQuestion();
        }


        return this.nextQuestionRequest().then((value:any) => {
            const questions = value.data.nextQuestions;
            questions.unshift(value.data.question);

            this.nextQuestions               = this.mapQuestions(questions);
            this.lastQuestionInNextQuestions = false;
            this._isLastQuestion             = value.data.isLast;
            this.progress                    = value.data.progress;

            if (!this.firstNextQuestion) {
                this.reloadNextQuestionsAfterQuestionIds.push(this.currentQuestion.id);
                this.firstNextQuestion = false;
            }

            return pushCurrentQuestion();
        });
    }

    private nextQuestionRequest():ng.IPromise<any> {
        return this.http({
            method: 'GET',
            url   : '/api/session/' + this.sessionService.activeSession.id + '/flow/next',
            params: {
                locale: this.translationService.getCurrentLanguage().locale
            }
        });
    }

    onNextQuestion(callback:() => boolean):() => void {
        const index = this.nextQuestionCallbacks.push(callback) - 1;
        return () => this.nextQuestionCallbacks.splice(index, 1);
    }

    public previousQuestion():ng.IPromise<any> {
        if (this.previousQuestions.length <= 0) {
            return this.q.reject();
        }

        const newCurrentQuestion = this.previousQuestions.pop();
        if (this.currentQuestion.meta.progress !== false || newCurrentQuestion.meta.progress !== false) {
            this.progress.currentQuestion -= this.progress.currentQuestion > 1 ? 1 : 0;
        }

        this.nextQuestions.unshift(this.currentQuestion);
        this.currentQuestion                 = newCurrentQuestion;
        this.lastQuestionInNextQuestions     = this._isLastQuestion;
        this._isLastQuestion                 = false;
        this.sharedQuestionService.questions = [];

        if (this.progress.currentQuestion > this.progress.answered) {
            this.progress.answered = this.progress.currentQuestion
        }

        return this.q.resolve();
    }

    private questionBeforeJump:IQuestion;

    previousQuestionJump(id:number):ng.IPromise<any> {
        let questionToJumpTo:IQuestion;
        for (const question of this.previousQuestions) {
            if (question.id === id) {
                questionToJumpTo = question;
            }
        }

        if (!questionToJumpTo) {
            return this.q.reject();
        }

        this.questionBeforeJump = this.currentQuestion;

        while (this.currentQuestion.id !== questionToJumpTo.id) {
            const newCurrentQuestion = this.previousQuestions.pop();
            if (this.currentQuestion.meta.progress !== false || newCurrentQuestion.meta.progress !== false) {
                this.progress.currentQuestion -= this.progress.currentQuestion > 1 ? 1 : 0;
            }

            this.nextQuestions.unshift(this.currentQuestion);
            this.currentQuestion = newCurrentQuestion;
        }

        this.lastQuestionInNextQuestions     = this._isLastQuestion;
        this._isLastQuestion                 = false;
        this.sharedQuestionService.questions = [];

        if (this.progress.currentQuestion > this.progress.answered) {
            this.progress.answered = this.progress.currentQuestion;
        }

        return this.q.resolve();
    }

    jumpBack():ng.IPromise<any> {
        if (!this.questionBeforeJump) {
            return this.q.reject();
        }

        // @TODO: what about repeated questions ???
        while (this.currentQuestion.id !== this.questionBeforeJump.id) {
            if (this.currentQuestion.meta.progress !== false) {
                this.progress.currentQuestion++;
            }

            this.previousQuestions.push(this.currentQuestion);
            this.currentQuestion = this.nextQuestions.shift();
        }

        this.sharedQuestionService.questions = [];
        this.questionBeforeJump              = null;

        return this.q.resolve();
    }

    public skipQuestion() {
        if (!this.currentQuestion.required && this.nextQuestions.length > 0) {
            this.nextQuestion();
        }
    }

    public isLastQuestion():boolean {
        return this._isLastQuestion;
    }

    public getProgress():ISessionProgress {
        return this.progress;
    }

    isFirstQuestion():boolean {
        return this.previousQuestions.length === 0;
    }

    canSaveInBackground():boolean {
        return this.nextQuestions.length > 0 && this.reloadNextQuestionsAfterQuestionIds.indexOf(this.currentQuestion.id) === -1;
    }

    getPreviousQuestions():IQuestion[] {
        return this.previousQuestions;
    }

    private mapQuestions(questions:IQuestion[]):IQuestion[] {
        for (let question of questions) {
            var newMeta:any = {};

            if (question.meta) {
                for (let property in question.meta) {
                    if (!question.meta.hasOwnProperty(property)) {
                        continue;
                    }

                    let newProperty      = property.replace(/-([a-z])/g, g => g[1].toUpperCase());
                    newMeta[newProperty] = question.meta[property];
                }
            }

            question.meta = newMeta;

            if (question.questions && question.questions.length > 0) {
                question.questions = this.mapQuestions(question.questions);
            }
        }

        return questions;
    }

    static factory() {
        const factory = ($http:ng.IHttpService,
                         $q:ng.IQService,
                         SessionService:ISessionService,
                         SharedQuestionService:SharedQuestionService,
                         TranslationService:ITranslationService) =>
            new QuestionnaireLogicService($http, $q, SessionService, SharedQuestionService, TranslationService);

        factory.$inject = ['$http', '$q', 'SessionService', 'SharedQuestionService', 'TranslationService'];
        return factory;
    }
}

app.factory('QuestionnaireLogicService', QuestionnaireLogicService.factory());